import { identity } from "@workspace/mobile";
import { dmnIdentity, performSilentReauth } from "../../utils/dmnIdentity";
import { getEnvironmentProps, isDmnURL } from "../../utils/environment";
import { apiFetchNotOkHandler, errorHandler } from "../errorHandler";

interface EvoTokenErrorResponse {
  error?: string;
  error_source?: string;
}

const isIdpError = async (response: Response): Promise<boolean> => {
  try {
    const body = (await response.clone().json()) as EvoTokenErrorResponse;
    return body.error_source === "idp";
  } catch {
    return false;
  }
};

const fetchButtonToken = async (
  authApiBaseUrl: string,
  token: string
): Promise<Response> => {
  return fetch(`${authApiBaseUrl}/evo/collins/button/token`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
};

const createIdentityFallbackResponse = async (): Promise<Response> => {
  const identityEvoResponse = {
    access_token: await identity.getIdToken(),
    expires_in: 3600,
    token_type: "Bearer"
  };
  return new Response(JSON.stringify(identityEvoResponse));
};

const attemptIdpReauth = async (
  authApiBaseUrl: string,
  originalResponse: Response
): Promise<Response> => {
  try {
    await performSilentReauth(authApiBaseUrl);
    const freshToken = await dmnIdentity.getAccessToken();
    const retryResponse = await fetchButtonToken(authApiBaseUrl, freshToken);

    if (!retryResponse.ok) {
      apiFetchNotOkHandler(retryResponse, "Error fetching button token");
    }

    return retryResponse;
  } catch {
    apiFetchNotOkHandler(originalResponse, "Error fetching button token");
    return originalResponse;
  }
};

const handleFailedResponse = async (
  authApiBaseUrl: string,
  response: Response
): Promise<Response> => {
  if (isDmnURL() && (await isIdpError(response))) {
    return attemptIdpReauth(authApiBaseUrl, response);
  }

  apiFetchNotOkHandler(response, "Error fetching button token");

  if (!isDmnURL()) {
    return createIdentityFallbackResponse();
  }

  return response;
};

export const getButtonToken = async (): Promise<Response> => {
  const token = await dmnIdentity.getAccessToken();
  const environmentProps = getEnvironmentProps(token) ?? null;
  const authApiBaseUrl = environmentProps?.AUTH_API_BASE_URL ?? "";

  try {
    const response = await fetchButtonToken(authApiBaseUrl, token);

    if (!response.ok) {
      return handleFailedResponse(authApiBaseUrl, response);
    }

    return response;
  } catch (error) {
    errorHandler(error, "Error fetching button token");

    if (!isDmnURL()) {
      return createIdentityFallbackResponse();
    }

    return new Response("Error fetching button token", { status: 500 });
  }
};
